import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertTriangle, Shield, Mail } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const DISCLAIMER_VERSION = "1.0.0";
const DISCLAIMER_KEY = "slidecon_beta_disclaimer_accepted";

// Test/automation bypass:
//  - URL query `?skip_beta=1` (one-shot — also persists to localStorage for the session)
//  - Pre-set `localStorage.setItem("slidecon_beta_disclaimer_accepted", "1.0.0")` from page.evaluate()
//  - Global helper window.__skipBetaDisclaimer() callable from any automation harness
if (typeof window !== "undefined") {
  window.__skipBetaDisclaimer = () => {
    try {
      localStorage.setItem(DISCLAIMER_KEY, DISCLAIMER_VERSION);
      return true;
    } catch {
      return false;
    }
  };
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("skip_beta") === "1") {
      localStorage.setItem(DISCLAIMER_KEY, DISCLAIMER_VERSION);
    }
  } catch { /* SSR / restricted contexts */ }
}

export default function BetaDisclaimer() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [understood, setUnderstood] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (mounted && user) {
      const acceptedVersion = localStorage.getItem(DISCLAIMER_KEY);
      if (acceptedVersion !== DISCLAIMER_VERSION) {
        setOpen(true);
      }
    }
  }, [mounted, user]);

  const handleAccept = useCallback(() => {
    if (accepted && understood) {
      localStorage.setItem(DISCLAIMER_KEY, DISCLAIMER_VERSION);
      setOpen(false);
    }
  }, [accepted, understood]);

  // Don't render anything until mounted and user exists
  if (!mounted || !user) return null;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-2xl" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/30">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <DialogTitle className="text-xl flex items-center gap-2">
                Welcome to SlideCon
                <Badge variant="outline" className="text-amber-500 border-amber-500">BETA</Badge>
              </DialogTitle>
              <DialogDescription>
                Please read this important notice before continuing
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[400px] pr-4">
          <div className="space-y-4 text-sm">
            {/* Beta Status Notice */}
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <h3 className="font-semibold text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Beta Software Notice
              </h3>
              <p className="text-amber-700 dark:text-amber-300">
                SlideCon is currently in <strong>BETA</strong> and is provided on an &quot;AS IS&quot; and &quot;AS AVAILABLE&quot; basis. 
                Features may change, be modified, or removed without prior notice. We are actively developing and 
                improving the platform based on user feedback.
              </p>
            </div>

            {/* Disclaimer */}
            <div className="p-4 bg-slate-50 dark:bg-slate-900 border rounded-lg">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Disclaimer of Warranties
              </h3>
              <p className="text-muted-foreground mb-3">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>
                  SlideCon, a DBA of <strong>RDK Services LLC</strong>, makes no warranties, express or implied, 
                  regarding the reliability, accuracy, or availability of this software.
                </li>
                <li>
                  We disclaim all warranties of merchantability, fitness for a particular purpose, and non-infringement.
                </li>
                <li>
                  You acknowledge that the use of beta software may result in unexpected behavior, data loss, 
                  or service interruptions.
                </li>
                <li>
                  RDK Services LLC shall not be liable for any direct, indirect, incidental, special, consequential, 
                  or punitive damages arising from your use of or inability to use the service.
                </li>
              </ul>
            </div>

            {/* Limitation of Liability */}
            <div className="p-4 bg-slate-50 dark:bg-slate-900 border rounded-lg">
              <h3 className="font-semibold mb-2">Limitation of Liability</h3>
              <p className="text-muted-foreground">
                IN NO EVENT SHALL RDK SERVICES LLC, ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE 
                FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT 
                LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-muted-foreground">
                <li>Your access to or use of or inability to access or use the Service</li>
                <li>Any conduct or content of any third party on the Service</li>
                <li>Any content obtained from the Service</li>
                <li>Unauthorized access, use, or alteration of your transmissions or content</li>
              </ul>
            </div>

            {/* Data & Privacy */}
            <div className="p-4 bg-slate-50 dark:bg-slate-900 border rounded-lg">
              <h3 className="font-semibold mb-2">Data & Privacy</h3>
              <p className="text-muted-foreground">
                While we implement reasonable security measures, we cannot guarantee the absolute security of your data 
                during the beta period. We recommend not storing sensitive or critical information solely on this platform. 
                Please review our <a href="/privacy" className="text-primary underline">Privacy Policy</a> and 
                <a href="/terms" className="text-primary underline ml-1">Terms of Service</a> for more information.
              </p>
            </div>

            {/* Support & Bug Reporting */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Support & Bug Reporting
              </h3>
              <p className="text-blue-700 dark:text-blue-300 mb-2">
                We value your feedback! If you encounter any issues, bugs, or have suggestions, please contact us:
              </p>
              <div className="flex items-center gap-2">
                <a 
                  href="mailto:cs@myrdkllc.com" 
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/50 rounded-md text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/70 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  cs@myrdkllc.com
                </a>
              </div>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                You can also use the Bug Report feature in the app menu to submit detailed reports.
              </p>
            </div>

            {/* Company Info */}
            <div className="p-3 bg-muted/50 rounded-lg text-xs text-muted-foreground">
              <p>
                <strong>SlideCon</strong> is a DBA (Doing Business As) of <strong>RDK Services LLC</strong>.<br />
                15810 Gale Ave. Suite 285, Hacienda Heights, CA 91745<br />
                All rights reserved. By using this service, you agree to be bound by our Terms of Service 
                and Privacy Policy.
              </p>
            </div>
          </div>
        </ScrollArea>

        <div className="space-y-3 pt-4 border-t">
          <div className="flex items-start gap-3">
            <Checkbox 
              id="understood" 
              checked={understood}
              onCheckedChange={(checked) => setUnderstood(checked === true)}
            />
            <label htmlFor="understood" className="text-sm leading-tight cursor-pointer">
              I understand that SlideCon is in BETA and may contain bugs, errors, or incomplete features. 
              I acknowledge that data loss or service interruptions may occur.
            </label>
          </div>
          
          <div className="flex items-start gap-3">
            <Checkbox 
              id="accepted" 
              checked={accepted}
              onCheckedChange={(checked) => setAccepted(checked === true)}
            />
            <label htmlFor="accepted" className="text-sm leading-tight cursor-pointer">
              I have read and agree to the above disclaimer, the{' '}
              <a href="/terms" className="text-primary underline">Terms of Service</a>, and{' '}
              <a href="/privacy" className="text-primary underline">Privacy Policy</a>. 
              I waive any claims against RDK Services LLC arising from my use of this beta software.
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button 
            onClick={handleAccept} 
            disabled={!accepted || !understood}
            className="w-full sm:w-auto"
            data-testid="accept-beta-disclaimer"
          >
            I Accept - Continue to SlideCon
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
